/**
 * WordPress dependencies
 */
import { sprintf, __ } from '@wordpress/i18n';
import {
	InnerBlocks,
	useBlockProps,
	useInnerBlocksProps,
	InspectorControls,
	store as blockEditorStore,
	URLInput,
} from '@wordpress/block-editor';
import {
	Button,
	PanelBody,
	Dropdown,
	MenuGroup,
	MenuItem,
	Icon,
	__experimentalConfirmDialog as ConfirmDialog,
	__experimentalVStack as VStack,
	__experimentalHStack as HStack,
	BaseControl,
	TextControl,
} from '@wordpress/components';
import { useSelect } from '@wordpress/data';
import { useState, useEffect } from '@wordpress/element';
import { ActionsModal } from './actions-modal';
import { integrations, icons } from './constants';
import { useEntityProp } from '@wordpress/core-data';
import { close, cog } from '@wordpress/icons';
import './editor.scss';

const TEMPLATE = [
	[
		'popper/form-input',
		{
			type: 'text',
			label: __( 'Name' ),
			name: 'name',
			required: true,
		},
	],
	[
		'popper/form-input',
		{
			type: 'email',
			label: __( 'Email' ),
			name: 'email',
			required: true,
		},
	],
	[ 'popper/form-button', {} ],
];

const Edit = ( { clientId, setAttributes, attributes } ) => {
	const [ showModal, setShowModal ] = useState( false );
	const [ confirm, setConfirm ] = useState( false );
	const blockProps = useBlockProps();
	const { redirectUrl, successMessage } = attributes;

	const { hasInnerBlocks, postType, postId, isPreview } = useSelect(
		( select ) => {
			const { getBlock, getSettings } = select( blockEditorStore );
			const block = getBlock( clientId );
			return {
				hasInnerBlocks: !! ( block && block.innerBlocks.length ),
				postType: select( 'core/editor' ).getCurrentPostType(),
				postId: select( 'core/editor' ).getCurrentPostId(),
				isPreview: getSettings().isPreviewMode,
			};
		},
		[ clientId ]
	);

	const innerBlocksProps = useInnerBlocksProps( blockProps, {
		prioritizedInserterBlocks: [
			'popper/form-input',
			'popper/form-button',
			'popper/form-select',
		],
		template: TEMPLATE,
		renderAppender: hasInnerBlocks
			? undefined
			: InnerBlocks.ButtonBlockAppender,
	} );

	const [ meta, setMeta ] = useEntityProp(
		'postType',
		postType,
		'meta',
		postId
	);

	useEffect( () => {
		if ( attributes.id !== postId ) {
			setAttributes( { id: postId } );
		}
	}, [] );

	const remove = ( item ) => {
		const arr = meta._popper_actions.filter( ( o ) => {
			return o.id !== item.id;
		} );
		setMeta( { ...meta, _popper_actions: arr } );
		setShowModal( false );
	};

	return (
		<>
			<InspectorControls>
				<PanelBody title={ __( 'Settings' ) }>
					<Dropdown
						popoverProps={ { placement: 'bottom-start' } }
						renderToggle={ ( { isOpen, onToggle } ) => (
							<Button
								variant="primary"
								onClick={ onToggle }
								aria-expanded={ isOpen }
							>
								{ __( 'Add action', 'popper' ) }
							</Button>
						) }
						renderContent={ ( { onToggle } ) => (
							<MenuGroup>
								{ integrations.map( ( a, i ) => {
									return (
										<MenuItem
											icon={
												<Icon
													icon={ icons[ a.name ] }
												/>
											}
											onClick={ () => {
												setShowModal( a );
												onToggle();
											} }
											iconPosition="left"
											key={ i }
										>
											{ a.name }
										</MenuItem>
									);
								} ) }
							</MenuGroup>
						) }
					/>
					<VStack className={ 'popper-actions-list' }>
						{ meta._popper_actions.map( ( a, i ) => {
							return (
								<HStack
									key={ i }
									direction="row"
									className={ 'popper-action-item' }
								>
									<HStack justify={ 'flex-start' }>
										<Icon icon={ icons[ a.name ] } />
										<b>{ a.name }</b>
									</HStack>
									<HStack justify={ 'flex-end' } spacing="0">
										<Button
											icon={ cog }
											onClick={ () => setShowModal( a ) }
											size="small"
										/>
										<Button
											icon={ close }
											onClick={ () => setConfirm( a ) }
											size="small"
										/>
									</HStack>
								</HStack>
							);
						} ) }
					</VStack>
					<TextControl
						label={ __( 'Success message', 'popper' ) }
						value={ successMessage }
						onChange={ (val) => setAttributes( { successMessage: val } ) }
						help={ __( 'The message displayed after successfully submitted form.', 'popper' ) }
						__nextHasNoMarginBottom
						__next40pxDefaultSize
					/>
					<BaseControl
						help={ __(
							'Select the url you want to rediret to after form submission.',
							'popper'
						) }
						__nextHasNoMarginBottom
					>
						<URLInput
							label={ __( 'Redirect Url', 'popper' ) }
							value={ redirectUrl }
							onChange={ ( newURL ) =>
								setAttributes( { redirectUrl: newURL } )
							}
							className={ 'popper-urlinput' }
							__nextHasNoMarginBottom
						/>
					</BaseControl>
				</PanelBody>
			</InspectorControls>
			{ showModal && (
				<ActionsModal
					settings={ showModal }
					clientId={ clientId }
					remove={ remove }
					onRequestClose={ () => {
						setShowModal( false );
					} }
					showConfirm={ () => setConfirm(true) }
				/>
			) }
			<ConfirmDialog
				isOpen={ confirm }
				onConfirm={ () => {
					remove( confirm );
					setConfirm( false );
				} }
				onCancel={ () => setConfirm( false ) }
			>
				{ sprintf(
					'Are you sure you want delete %s action?',
					confirm.name,
					'popper'
				) }
			</ConfirmDialog>
			<div { ...innerBlocksProps } />
		</>
	);
};
export default Edit;
